/**
 * English (en) — parity with `./es.ts`.
 *
 * If a key is missing here the LocaleContext falls back to Spanish.
 * Keep the shape identical to `es` to keep TypeScript happy.
 */
import type { Dictionary } from './es';

export const en: Dictionary = {
  common: {
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    delete: 'Delete',
    edit: 'Edit',
    copy: 'Copy',
    copied: 'Copied',
    open: 'Open',
    loading: 'Loading…',
    error: 'Error',
    retry: 'Retry',
    back: 'Back',
    next: 'Next',
    yes: 'Yes',
    no: 'No',
    confirm: 'Confirm',
    search: 'Search',
    settings: 'Settings',
    upload: 'Upload',
    download: 'Download',
    active: 'Active',
    inactive: 'Inactive',
    preview: 'Preview',
    syncing: 'Syncing',
    retrying: 'Retrying',
    pending: 'pending',
    pendingPlural: 'pending',
    ready: 'Ready',
    offline: 'Offline',
    syncRetry: 'Tap to retry sync',
  },
  // Sprint P0 Cierre · i18n for engine states and sidebar
  engine: {
    idle: 'Ready',
    thinking: 'Thinking…',
    working: 'Working…',
    reviewing: 'Reviewing…',
    building: 'Building…',
    ready: 'Ready',
    failed: 'Failed',
    canceled: 'Canceled',
    queued: 'Queued',
    processing: 'Processing',
    indexing: 'Indexing',
    scanning: 'Scanning',
    generating: 'Generating…',
  },
  sidebar: {
    home: 'Home',
    newProject: 'New project',
    deployedApps: 'Deployed apps',
    recentProjects: 'Recent projects',
    templates: 'Templates',
    community: 'Community',
    support: 'Support',
    profile: 'Profile',
    admin: 'Admin',
    logout: 'Log out',
  },
  brand: {
    tagline: 'AI Engineering Center',
  },
  // Sprint Estabilización P0.2 · Feb 2026 · avatar menu keys
  menu: {
    myAccount: 'My Account',
    credits: 'Credits',
    creditsBilling: 'Credits & Billing',
    language: 'Language',
    voiceAudio: 'Voice & Audio',
    support: 'Support',
    contactSupport: 'Contact support',
    supportAi: 'AI Support Center',
    adminPanel: 'Admin panel',
    admin: 'Administration',
    templates: 'Templates',
    userFallback: 'User',
    planPro: 'Pro Plan',
    planFree: 'Free',
  },
  auth: {
    welcome: 'Welcome to Nexus',
    subtitle: 'Build, create, and deploy apps with AI.',
    continueWithEmail: 'Continue with email',
    continueWithGoogle: 'Continue with Google',
    noAccount: 'Don\'t have an account?',
    haveAccount: 'Already have an account?',
    createAccount: 'Create an account',
    name: 'Name',
    secure: 'SECURE',
    private: 'PRIVATE',
    reliable: 'RELIABLE',
    email: 'Email',
    password: 'Password',
    login: 'Log in',
    register: 'Sign up',
    loginError: 'Could not sign in',
    // Sprint P0 Quirúrgico Feb 2026
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    forgotPassword: 'Forgot your password?',
    recoverTitle: 'Recover password',
    recoverBody: 'We\'ll send you a link to reset it.',
    emailPlaceholder: 'you@example.com',
    sendRecoveryLink: 'Send recovery link',
    linkGenerated: 'Generated link (valid 30 min):',
    linkCopied: 'Link copied.',
    openNow: 'Open now',
    backToLogin: 'Back to login',
    newPasswordTitle: 'New password',
    newPasswordBody: 'Pick a password of at least 8 characters.',
    newPasswordPlaceholder: 'New password',
    confirmPasswordPlaceholder: 'Confirm password',
    updatePassword: 'Update password',
    cannotContinue: 'Could not continue. Please try again.',
    forgotToastGenerated: 'Recovery link generated.',
    forgotToastMaybe: 'If the email exists, we\'ll send a link.',
    forgotToastFail: 'Could not process the request.',
    resetInvalid: 'Invalid recovery link.',
    resetMin: 'Minimum 8 characters.',
    resetMismatch: 'Passwords do not match.',
    resetOk: 'Password updated. Please sign in.',
    resetFail: 'Could not reset.',
  },
  home: {
    title: 'What will you build today?',
    titlePre: 'What will you',
    titleMid: 'build',
    titlePost: 'today?',
    subtitle: 'Nexus turns your ideas into stunning apps with the power of AI.',
    welcomeUser: 'Welcome, {name}',
    defaultName: 'builder',
    newProject: 'New project',
    promptLabel: 'Tell me your idea…',
    promptPlaceholder: 'Describe what you want to build or paste a URL to improve an existing project.',
    build: 'Build',
    attach: 'Attach',
    attachHint: '{count} file(s) attached',
    pasteUrl: 'Paste project URL',
    pasteUrlPrompt: 'Paste the project URL, repo or inspiration:',
    voice: 'Voice',
    stopDictate: 'Stop dictation',
    voiceUnsupported: 'Your browser does not support speech recognition.',
    micError: 'Microphone error',
    urlAdded: 'URL added to the prompt',
    urlInvalid: 'The URL must start with http:// or https://',
    workshopUrlInvalid: 'Paste a valid repository URL (https:// or git@).',
    send: 'Send',
    removeAttach: 'Remove',
    menu: 'Menu',
    selectedMode: 'Selected mode:',
    modeApps: 'Applications',
    modeAppsHint: 'Apps & Landing Pages',
    modeAppsPlaceholder: 'Describe the app or landing page you want to create…',
    modeWorkshop: 'Workshop',
    modeWorkshopHint: 'Repair projects',
    modeWorkshopPlaceholder: 'Paste your GitHub repository URL to analyze and repair your project…',
    recentTitle: 'Your recent projects',
    recentSeeAll: 'See all',
    deployedTitle: 'Deployed apps',
    deployedManage: 'Manage featured',
    deployedManageTitle: 'Feature apps on Home',
    deployedManageHint: 'Pick up to 4 deployed apps to keep them always visible.',
    deployedPlaceholderHint: 'Publish your first app and it will appear here',
    deployedEmpty: 'You have no deployed apps yet.',
    deployedSaved: 'Selection saved.',
    deployedSaveError: 'Could not save selection.',
    deployedMaxFour: 'Maximum 4 featured apps.',
    deployedPinnedList: 'Featured ({count}/4)',
    deployedPinnedEmpty: 'You have not pinned any app yet.',
    deployedAllApps: 'All your deployed apps',
    deployedSaveBtn: 'Save selection',
    updated: 'Updated',
    tagApp: 'App',
    tagWeb: 'Web',
    tagWorkshop: 'Workshop',
    tipLabel: 'Tip',
    tipText: 'Start with a simple idea and Nexus will make it real.',
    timeNow: 'now',
    timeMinutes: '{count}m ago',
    timeHours: '{count}h ago',
    timeDays: '{count}d ago',
    quickFullStack: 'Full-stack App',
    quickFullStackHint: 'Complete web apps with frontend and backend.',
    quickFullStackPrompt: 'Create a full-stack app with frontend and backend',
    quickBusiness: 'Business pages',
    quickBusinessHint: 'Professional websites — fast and effective.',
    quickBusinessPrompt: 'Create a professional landing page for a business',
    autoAgent: 'Nexus picks the right agent automatically — create, edit, fix or deploy.',
    createError: 'Could not create the workspace',
    // Sprint P0 Quirúrgico Feb 2026 · claves adicionales para Home
    close: 'Close',
    pinned: 'Pinned',
    pin: 'Pin',
    mockDashboardTitle: 'Dashboard',
    mockDashboardHint: 'Real-time analytics panel',
    mockShopTitle: 'Shop',
    mockShopHint: 'E-commerce with cart and payments',
    mockLandingTitle: 'Landing',
    mockLandingHint: 'Professional page with CTA',
    mockChatTitle: 'AI Chat',
    mockChatHint: 'Conversation with an assistant',
    fallbackName: 'Untitled',
    fallbackRepo: 'Repo',
    // Sprint Estabilización Final · Feb 2026 · tip banner in Home
    tipBody: 'For best results, describe your app in as much detail as possible. If you prefer, ask ChatGPT to generate a complete prompt and paste it here.',
  },
  dashboard: {
    title: 'Workspaces',
    subtitle: 'Isolated projects with their own memory and settings',
    empty: 'You don\'t have any projects yet. Create one to get started.',
    emptyTitle: 'No workspaces yet',
    emptyHint: 'Create your first workspace to get started',
    createNew: 'Create new',
    newWorkspace: 'New Workspace',
    createDialogTitle: 'Create Workspace',
    workspaceNamePlaceholder: 'My Project',
    descriptionOptional: 'Description (optional)',
    descriptionPlaceholder: 'Workspace description...',
    creating: 'Creating...',
    delete: 'Delete workspace',
    deletePrefix: 'You are about to delete',
    deleteSuffix: 'and all of its data:',
    deleteMissions: 'Missions and their history',
    deleteMemories: 'Memories',
    deleteIndex: 'Imported project index',
    deleteSandbox: 'Sandbox files',
    deleteWarning: 'This action cannot be undone.',
    deleteConfirmBtn: 'Delete permanently',
    deleting: 'Deleting…',
    deleteConfirm: 'Delete this workspace? This action is permanent.',
    deleteSuccess: 'Workspace deleted',
    deleteError: 'Failed to delete workspace',
    createSuccess: 'Workspace created — opening…',
    createError: 'Failed to create workspace',
    loadError: 'Failed to load workspaces',
    timeLessThanHour: 'Less than an hour ago',
    timeHour: '{n} hour ago',
    timeHours: '{n} hours ago',
    timeDay: '{n} day ago',
    timeDays: '{n} days ago',
  },
  workspace: {
    preview: 'Preview',
    previewHint: 'Open the development preview in a new tab',
    chat: 'Chat',
    files: 'Files',
    history: 'History',
    integrations: 'Integrations',
    publish: 'Publish',
    publishing: 'Publishing…',
    republish: 'Republish',
    published: 'Published',
    version: 'v{n}',
    copyUrl: 'Copy URL',
    openLive: 'Open public URL',
    loadError: 'Failed to load workspace',
    backHome: 'Back to home',
    durable: 'durable',
    durableFiles: 'file(s) saved durably',
    moreActions: 'More actions (Files · Summary · Export · Rollback)',
  },
  publish: {
    button: 'Publish',
    inFlight: 'Publishing… this takes 30-90 s',
    successCloudflare: '🚀 Deployed to Cloudflare Pages · v{version}',
    successFly: '🚀 Deployed to Fly.io · v{version}',
    successNexus: '🚀 Deployed to Nexus (internal) · v{version}',
    successGeneric: '🚀 Deployed · v{version}',
    fallback: '{host} failed, but I deployed to Nexus · v{version}',
    failed: 'Could not publish. Check the app status.',
    notReady: 'Not ready yet: {reasons}',
    urlCopied: 'URL copied to clipboard',
    clipboardError: 'Could not copy to clipboard',
    summaryCopied: 'Summary copied',
  },
  profile: {
    title: 'Profile',
    signOut: 'Sign out',
    plan: 'Plan',
    credits: 'Credits',
    buyCredits: 'Buy credits',
    referrals: 'Referral program',
    referralsHint: 'Earn up to 500 credits',
    referralsComingSoon: 'Referral program — coming soon',
    configuration: 'Configuration',
    account: 'Account settings',
    accountComingSoon: 'Account settings — coming soon',
    language: 'Language',
    languagePickerTitle: 'Pick a language',
    help: 'Help',
    appearance: 'Appearance',
    theme: 'Theme',
    accent: 'Accent',
    neon: 'Neon',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
    logout: 'Log out',
    photoComingSoon: 'Photo change — coming soon',
    buyComingSoon: 'Credit purchase — coming soon',
    // Sprint P0 Lanzamiento · new Profile menu keys
    menuAccountTitle: 'My account',
    menuAccountSubtitle: 'Personal info, security and customization.',
    menuCreditsTitle: 'Credits',
    menuCreditsSubtitle: 'Check your balance, usage history and purchases.',
    menuCreditsAvailable: 'available',
    menuLanguageTitle: 'Language',
    menuLanguageSubtitle: 'Change the interface language.',
    menuAudioTitle: 'Voice & Audio',
    menuAudioSubtitle: 'Configure the Nexus voice, sounds and accessibility.',
    menuSupportTitle: 'Support',
    menuSupportSubtitle: 'Chat with us whenever you need.',
    menuAdminTitle: 'Admin panel',
    menuAdminSubtitle: 'Global metrics, templates, financial engine and support.',
    backendEnvironment: 'Backend environment',
    backendProd: 'Production',
    backendDev: 'Development',
    backendActive: 'active',
    backendChanged: 'Backend changed',
    backendChangeFailed: 'Could not change backend',
    photoTooLarge: 'Photo is too large (max 12 MB).',
    photoUpdated: 'Photo updated.',
    creditsExhausted: 'Credits exhausted. Top up your credits to continue.',
    wakePreview: 'Wake Preview',
    wakePreviewHint: 'Bring your Preview back without rebuilding or spending credits.',
    wakingPreview: 'Waking Preview…',
    previewAwake: 'Preview is back online',
    wakeFailed: 'Could not wake the Preview',
  },
  integrations: {
    title: 'Integrations',
    subtitle: 'Connect external services to your account. Each integration applies to all your workspaces.',
    github: 'GitHub',
    githubHint: 'Required to clone private repos, Push to GitHub and Deploy from the chat.',
    githubConnected: 'GitHub connected',
    githubDisconnected: 'GitHub disconnected',
    githubConnectedAs: 'Connected as {name}',
    githubRejected: 'Token saved · rejected by GitHub',
    tokenPlaceholder: 'Paste your GitHub Personal Access Token',
    tokenSaved: 'Token saved',
    tokenSavedAs: 'Token verified as {name} · stored encrypted',
    tokenDeleted: 'Token deleted',
    tokenError: 'GitHub rejected the token: {msg}',
    tokenErrorGeneric: 'unknown error',
    tokenNeedPaste: 'Paste your GitHub Personal Access Token',
    tokenDeleteFail: 'Could not delete the token',
    tokenDeleteConfirm: 'Delete the GitHub token? Push to GitHub will stop working until you configure another.',
    replace: 'Replace',
    delete: 'Delete',
    validating: 'Validating…',
    save: 'Save',
    cancel: 'Cancel',
    createLink: 'Create the token at',
    scopeHint: '(scope repo). Nexus validates it against GitHub before storing it encrypted.',
    comingSoonNote:
      'Coming soon: more integrations (Stripe, ElevenLabs, Twilio…). If you need a specific one, tell us.',
    back: 'Back',
  },
  delivery: {
    delivered: 'Delivered',
    listenSummary: 'Listen to summary',
    stop: 'Stop',
    copySummary: 'Copy summary',
    showDetails: 'Show details',
    hideDetails: 'Hide details',
    changes: 'Changes',
    time: 'Time',
    patches: 'Applied changes',
    verifs: 'Verifications',
    evidence: 'Cited files',
    iterations: 'Iterations',
    modifiedFiles: 'Modified files',
    andMore: '… and {count} more',
    ttsError: 'Could not generate voice. Please try again.',
    titleObservations: 'Work with observations',
    titleModApplied: 'Modification applied',
    titlePublished: 'Publication completed',
    titleAnalyzed: 'Analysis completed',
    titleAppReady: 'Your app is ready',
    titleDone: 'Work completed',
    recapImplemented: 'What I implemented',
    recapFindings: 'What I found during development',
    recapRecommendations: 'Recommendations',
  },
  workshop: {
    loading: 'Loading…',
    delete: 'Delete session',
    deleteConfirm: 'Delete this Workshop session? The local sandbox will be removed as well.',
    deleted: 'Session deleted',
    deleteError: 'Could not delete the session',
    phaseCloning: 'Cloning your repository…',
    phaseAnalyzing: 'Analyzing your project structure…',
    failureTitle: 'Could not finish the analysis',
    retry: 'Retry',
    analysisTitle: 'Diagnosis',
    framework: 'Framework',
    runtime: 'Runtime',
    entryPoint: 'Entry point',
    services: 'Detected services',
    notDetected: 'Not detected',
    none: 'None',
    canBoot: 'Your project is ready — I can try to bring the Preview up.',
    needsHelp: 'I need more info before trying the Preview.',
    findingsTitle: '{count} finding(s)',
    branchTitle: 'Working branch',
    branchHint: 'All changes are applied on this branch — never on main.',
    envTitle: 'Required variables',
    envHint: 'Paste values for the missing variables (KEY=VALUE per line).',
    envSave: 'Save variables',
    envSaved: '{count} variable(s) saved',
    envInvalid: 'Expected format: NAME=value per line.',
    envError: 'Could not save variables',
    actionsTitle: 'Actions',
    repairBtn: 'Start repair',
    repairInFlight: 'Starting repair…',
    repairReady: 'Preview is ready. I can begin fixing the issues found.',
    repairBlocked: 'First I need to get your app running in the Preview.',
    repairStarted: 'Repair started',
    repairError: 'Could not start the repair',
    statusCloning: 'Cloning',
    statusAnalyzing: 'Analyzing',
    statusReady: 'Ready',
    statusPreviewOk: 'Preview OK',
    statusRepairing: 'Repairing',
    statusReadyPush: 'Ready to publish',
    statusPushed: 'Published',
    statusFailed: 'Failed',
  },
  support: {
    title: 'Support Center',
    titleAdmin: 'Support · Admin panel',
    newThread: 'New conversation',
    newHint: 'Write to us and we will get back as soon as possible. You can attach images or files in any message.',
    subjectPlaceholder: 'Subject (e.g. My deploy failed)',
    messagePlaceholder: 'Tell us what is happening…',
    replyPlaceholder: 'Type a message…',
    send: 'Send',
    attach: 'Attach',
    closeThread: 'Close thread',
    pickThreadHint: 'Pick a conversation on the left or start a new one.',
    emptyUser: 'You have no open conversations yet.',
    emptyAdmin: 'No conversations to review.',
    threadCreated: 'Conversation created',
    errorCreate: 'Could not create the conversation',
    errorSend: 'Could not send the message',
    errorFieldsRequired: 'Fill in the subject and message',
    attachTooLarge: '{name} is over 4 MB',
    statusOpen: 'Open',
    statusAnswered: 'Answered',
    statusInProgress: 'In progress',
    statusClosed: 'Closed',
    statusUnknown: 'Status',
    teamName: 'Nexus Team',
  },
  // Sprint P0 Quirúrgico Feb 2026 · claves añadidas para completar i18n
  workspaceUser: {
    deliveryReady: 'Functional app delivered · Preview ready',
    projectReady: 'Your project is ready',
    pending: 'Pending',
    recommendations: 'Recommendations',
    publish: 'Publish',
    publishSandbox: 'Publish to Sandbox',
    promoteProduction: 'Promote to Production',
    copy: 'Copy',
    preview: 'Preview',
    rollback: 'Rollback',
    engineRunning: 'Nexus is working…',
    engineRunningLabel: 'Engine running',
    cancel: 'Cancel',
    nexusAi: 'Nexus AI · ',
    thinking: 'Thinking…',
    composerPlaceholder: 'Type a message…',
    composerDisabled: 'Engine working…',
    newMessages: 'New messages',
    startPrompt: 'Start by describing your idea. Nexus will get to work.',
    loadError: 'Could not load the workspace.',
    nameUpdated: 'Name updated',
    previewPreparing: 'Preparing the Preview…',
    backHome: 'Back to home',
    rename: 'Rename',
    workspaceFallback: 'Workspace',
    statusActive: 'Active',
    statusLoading: 'Loading',
    editModeHint: 'Opens Preview with editing tools enabled',
    editMode: 'Edit mode',
    attach: 'Attach',
    voiceStart: 'Dictate by voice',
    voiceStop: 'Stop voice',
    sendAria: 'Send',
    cancelBuild: 'Cancel build',
    buildCanceled: 'Build canceled',
    buildCanceledDetail: 'You stopped the current build. You can send a new instruction whenever you want.',
    composerWorking: 'Nexus is working… wait for the Delivery Card',
    composerType: 'Type your message…',
    publishSandboxOk: 'Published to Sandbox successfully.',
    publishSandboxRegistered: 'Request registered. Configure NEXUS_SANDBOX_WEBHOOK for real deploy.',
    publishSandboxFail: 'Could not publish to Sandbox.',
    promoteConfirm: 'Promote the latest Sandbox version to Production?\n\nThis action notifies the production deploy pipeline.',
    promoteProdOk: 'Promoted to Production successfully.',
    promoteProdRegistered: 'Promotion registered. Configure NEXUS_PROD_WEBHOOK for real deploy.',
    promoteProdFail: 'Could not promote to Production.',
    changesDone: 'Changes made: ',
    pendingLabel: 'Pending: ',
    recommendationsLabel: 'Recommendations: ',
    ttsRetap: 'Tap play again to listen',
    ttsPreparing: 'Preparing…',
    ttsStop: 'Stop',
    ttsListen: 'Listen',
    ttsError: 'Could not play the audio.',
    renameFail: 'Could not rename.',
    attachFail: 'Could not attach the file.',
    // Sprint P0 Quirúrgico Feb 2026 · rotating live indicator states
    statusAnalyzing: '🧠 Analyzing…',
    statusDesigning: '🎨 Designing…',
    statusBuilding: '🧱 Building…',
    statusIntegrating: '⚙️ Integrating…',
    statusTesting: '🧪 Running tests…',
    statusCompiling: '📦 Compiling…',
    statusPreview: '🚀 Generating Preview…',
    statusValidating: '✅ Validating delivery…',
  },
  rollback: {
    confirmTitle: 'Restore Previous Version?',
    confirmBody: 'This will restore the previous version of your preview and discard the latest generated changes. Credits already used for previous generations will not be refunded. This action cannot be undone.',
    confirmCta: 'Restore Previous Version',
    cancel: 'Cancel',
    inFlight: 'Restoring…',
    success: 'Preview restored to previous version',
    empty: 'No previous version saved yet',
    error: 'Could not restore the Preview',
  },
  credits: {
    label: 'Credits:',
    refill: 'Refill credits',
    negative: 'You owe',
    balance: 'Balance',
    // Sprint Estabilización Final · Feb 2026 · full /creditos page
    pageTitle: 'Credits',
    availableCredits: 'AVAILABLE CREDITS',
    creditsWord: 'credits',
    renewal: 'Renewal',
    pickPackage: 'Pick your credit package',
    pickPackageSub: 'More credits, more possibilities.',
    trustStripSub: 'Fast, protected and reliable transactions.',
    buyCredits: 'Buy credits',
    chooseAmount: 'Choose the amount',
    plan: 'Plan',
    planFree: 'Free',
    planPro: 'Pro',
    addCreditsCta: 'Add credits',
    packagesTitle: 'Credit packages',
    packageMostPopular: 'MOST POPULAR',
    packageBuy: 'Buy',
    trustStripTitle: '100% secure payments with Stripe',
    trustSecure: 'Secure payment',
    trustFast: 'Fast process',
    trustPrivacy: 'Data protection',
    customAmountTitle: 'Custom amount',
    customAmountBody: 'Pick exactly the amount you need.',
    customAmountCta: 'Choose amount',
    historyTitle: 'Purchase history',
    historyBody: 'Review your previous purchases.',
    footerNote: 'Credits are activated instantly after payment.',
    back: 'Back',
    close: 'Close',
    purchaseTitle: 'Confirm purchase',
    purchaseCta: 'Pay with Stripe',
    purchaseBusy: 'Redirecting…',
    purchaseFail: 'Could not start the payment',
    successTitle: 'Purchase complete!',
    successBody: 'Your credits are available now.',
    successCta: 'Continue',
    packageDesc100: 'Ideal for testing and quick tweaks',
    packageDesc250: 'Perfect for small projects',
    packageDesc500: 'Great for small and medium projects',
    packageDesc1250: 'Excellent for ongoing work',
    packageDesc2500: 'Optimal for teams and agencies',
    packageDesc5000: 'Maximum value',
  },
  settings: {
    title: 'Settings',
    back: 'Back',
    navProfile: 'Profile',
    navSecurity: 'Security',
    navAppearance: 'Appearance',
    navNotifications: 'Notifications',
    navIntegrations: 'Integrations',
    navApikeys: 'API Keys',
    navPreferences: 'Preferences',
    navLanguage: 'Language',
    navVoice: 'Voice & Audio',
    navPrivacy: 'Data & privacy',
    profileTitle: 'Profile',
    photoHint: 'Photo synced with your email. Custom uploads coming soon.',
    fieldName: 'Name',
    fieldEmail: 'Email',
    fieldPlan: 'Plan',
    planAdmin: 'Admin · Pro Plan',
    planFree: 'Free',
    saveChanges: 'Save changes',
    profileSaved: 'Profile updated',
    profileSaveFail: 'Could not save',
    securityTitle: 'Security',
    curPassword: 'Current password',
    newPassword: 'New password (min 8 characters)',
    updatePassword: 'Update password',
    passwordTooShort: 'Password too short (min 8)',
    passwordUpdated: 'Password updated',
    passwordUpdateFail: 'Could not update the password',
    passwordEndpointSoon: 'Password change endpoint coming soon',
    appearanceTitle: 'Appearance',
    appearanceBody: 'Nexus uses the Neon Cyan dark theme by default. Soon you\'ll be able to change the accent color (cyan, purple, neon green or red) and upload a custom Home background.',
    themeLabel: 'Theme',
    themeDarkCyan: 'Dark (Neon Cyan)',
    themeAuto: 'Automatic (follow system)',
    languageTitle: 'Language',
    languageBody: 'Nexus speaks your language. Engine messages, UI and voices adapt to your selection automatically.',
    languageField: 'Interface language',
    voiceTitle: 'Voice & Audio',
    voiceBody: 'Choose how Nexus sounds. The selected voice is used for "Listen to summary" in the chat.',
    voiceField: 'Default voice',
    voiceUpdated: 'Default voice updated',
    voiceUpdateFail: 'Could not change the voice',
    apikeysTitle: 'API Keys',
    apikeysBody: 'Nexus uses the Emergent Universal Key for all AI models. You don\'t need to manage your own keys. For external integrations (Stripe, GitHub, etc.) go to Settings → Integrations.',
    integrationsTitle: 'Integrations',
    integrationsBody: 'Connect Nexus to GitHub, Stripe, mail services and more. The engine detects and uses connected services automatically.',
    integrationsOpen: 'Open Integrations panel →',
    notificationsBody: 'Set up email and in-app alerts for credit changes, deploys and support replies.',
    preferencesBody: 'Customize the autonomous flow of Nexus (auto-deploy, verifiers, risk tolerance).',
    privacyBody: 'Export your data, manage active sessions and delete your account if you wish.',
    comingSoon: 'Panel available in a future iteration.',
  },
  idioma: {
    title: 'Language',
    back: 'Back',
    changedEs: 'Idioma cambiado a Español',
    changedEn: 'Language changed to English',
    autoDetectHint: 'The first time, Nexus detects your browser language automatically. Change it here whenever you want.',
  },
  audio: {
    title: 'Voice & Audio',
    back: 'Back',
    sectionDefaultVoice: 'Default voice',
    sectionSpeed: 'Speed',
    sectionToggles: 'Enable / disable',
    switchAutoplay: 'Auto-play voice replies',
    switchMic: 'Microphone',
    switchSystemSounds: 'System sounds',
    switchHiContrast: 'High contrast (accessibility)',
    savedLocally: 'Changes save automatically on this device.',
    fallbackJarvisName: 'Futuristic Assistant',
    fallbackJarvisDesc: 'Deep, serene voice, JARVIS-style',
    fallbackNovaName: 'Natural Voice',
    fallbackNovaDesc: 'Warm human voice, great for conversational reading',
    fallbackFableName: 'Character Humanoid',
    fallbackFableDesc: 'Distinctive British accent with strong personality',
    playAria: 'Play sample of {name}',
    pauseAria: 'Pause sample of {name}',
    defaultSet: 'Default voice: {name}',
    noCredits: 'Not enough credits for audio',
    sampleFail: 'Could not generate the sample',
    retap: 'Tap play again to listen',
    playFail: 'Could not play the audio',
    sample: 'Hi, I am your Nexus assistant. I can read your messages aloud.',
  },
};
